<template>
  <v-container class="login-page d-flex align-center justify-center" fluid>
    <v-card class="login-card pa-7 pa-sm-10 text-center" dark outlined>
      <v-progress-circular
        v-if="!authReady"
        color="red"
        indeterminate
        size="48"
      />

      <template v-else-if="isAuthenticated">
        <v-avatar v-if="currentUser.photoURL" class="mb-5" size="88">
          <v-img :src="currentUser.photoURL" :alt="`${displayName} profile photo`" />
        </v-avatar>
        <v-icon v-else class="mb-5" size="88">mdi-account-circle</v-icon>

        <h1>Welcome, {{ displayName }}</h1>
        <p class="login-copy mb-7">{{ currentUser.email }}</p>

        <div class="login-actions">
          <v-btn color="red" large @click="returnToPreviousPage">
            <v-icon left>mdi-arrow-left</v-icon>
            {{ returnLabel }}
          </v-btn>
          <v-btn outlined large :loading="isSigningOut" @click="signOut">
            Sign out
          </v-btn>
        </div>
      </template>

      <template v-else>
        <v-icon class="mb-5" color="red" size="64">mdi-account-circle</v-icon>
        <h1>Sign in to Reckon</h1>
        <p class="login-copy mb-7">
          Use your Google account to continue. Your session stays signed in on this device.
        </p>
        <google-sign-in-button @signed-in="returnToPreviousPage" />
        <v-btn class="mt-4" text @click="returnToPreviousPage">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ returnLabel }}
        </v-btn>
      </template>

      <v-alert v-if="authError" class="mt-7 mb-0 text-left" type="error" outlined>
        {{ authError }}
      </v-alert>
    </v-card>
  </v-container>
</template>

<script>
import GoogleSignInButton from '@/components/GoogleSignInButton.vue';

export default {
  name: 'LoginView',
  components: {
    GoogleSignInButton,
  },
  data: () => ({
    isSigningOut: false,
  }),
  computed: {
    authReady() {
      return this.$store.getters.authReady;
    },
    authError() {
      return this.$store.getters.authError;
    },
    currentUser() {
      return this.$store.getters.currentUser;
    },
    isAuthenticated() {
      return this.$store.getters.isAuthenticated;
    },
    displayName() {
      return this.currentUser.displayName || this.currentUser.email || 'there';
    },
    returnPath() {
      const { redirect } = this.$route.query;
      if (typeof redirect !== 'string' || !redirect.startsWith('/') || redirect.startsWith('//')) {
        return '/';
      }

      const resolvedRoute = this.$router.resolve(redirect).route;
      return resolvedRoute.name === 'login' ? '/' : redirect;
    },
    returnLabel() {
      if (this.returnPath === '/') return 'Return Home';

      const resolvedRoute = this.$router.resolve(this.returnPath).route;
      const pageTitle = resolvedRoute.meta?.title?.split('|')[0].trim();
      return pageTitle ? `Return to ${pageTitle}` : 'Return to previous page';
    },
  },
  methods: {
    returnToPreviousPage() {
      if (this.$route.fullPath !== this.returnPath) this.$router.push(this.returnPath);
    },
    async signOut() {
      this.isSigningOut = true;

      try {
        await this.$store.dispatch('signOut');
      } catch (error) {
        // The store exposes the user-facing error above.
      } finally {
        this.isSigningOut = false;
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  min-height: calc(100vh - 64px);
  padding: 32px 20px;
}

.login-card {
  background: #0b0b0b;
  border-color: #333;
  max-width: 520px;
  width: 100%;
}

.login-card h1 {
  font-family: "Oswald", sans-serif;
  font-size: clamp(2rem, 6vw, 3rem);
  line-height: 1.1;
  margin-bottom: 18px;
}

.login-copy {
  color: #c7c7c7;
  font-size: 1rem;
  line-height: 1.7;
}

.login-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}
</style>
